import React, { useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { categoryOptions, paymentTypeOptions, kidOptions } from './constants';

function ExpenseEntry({ who, onSubmitSuccess }) {
  const today = new Date();
  const defaultDate = `${today.getMonth() + 1}/${today.getDate()}`;
  const [formData, setFormData] = useState({
    date: defaultDate,
    amount: '',
    kidsCheshbon: '',
    reason: '',
    category: '',
    paymentType: 'Cash',
    kid: null,
  });
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const categories = categoryOptions;
  const paymentTypes = paymentTypeOptions;
  const kids = kidOptions;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return; // guard against double-submit
    setIsLoading(true);
    let succeeded = false;
    try {
      const submissionData = {
        ...formData,
        who,
        kidsCheshbon: formData.kidsCheshbon === '' ? undefined : formData.kidsCheshbon,
        isKidsAccount: formData.kidsCheshbon && formData.kid ? 'YES' : '', // Auto-set based on kidsCheshbon + kid
      };
      // Use consistent URL handling - the axios interceptor will add the base URL
      await axios.post('/api/budgets', submissionData, { withCredentials: true });
      setMessage('Entry added successfully!');
      setFormData({
        date: defaultDate,
        amount: '',
        kidsCheshbon: '',
        reason: '',
        category: '',
        paymentType: 'Cash',
        kid: null,
      });
      succeeded = true;
      onSubmitSuccess();
    } catch (err) {
      setMessage('Error adding entry: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
      // Success messages auto-clear; errors stay until the next submit
      if (succeeded) {
        setTimeout(() => setMessage(''), 3000);
      }
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      background: 'rgba(255, 255, 255, 0.1)',
      // Removed backdropFilter for iOS Safari compatibility
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '0.75rem',
      color: 'white',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(255, 255, 255, 0.1)' : 'none',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        borderColor: 'rgba(255, 255, 255, 0.3)'
      },
      transition: 'all 0.3s ease',
      minHeight: '48px',
      // Improve touch target for iOS
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'rgba(255, 255, 255, 0.2)'
        : state.isFocused
        ? 'rgba(255, 255, 255, 0.1)'
        : 'transparent',
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
    }),
    menu: (provided) => ({
      ...provided,
      background: 'rgba(0, 0, 0, 0.9)',
      // Removed backdropFilter for iOS Safari compatibility
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '0.75rem',
      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0.5rem',
      // Improve scrolling on iOS
      WebkitOverflowScrolling: 'touch',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'white',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.7)',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': { color: 'white' },
      cursor: 'pointer',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': { color: 'white' },
      cursor: 'pointer',
    }),
    input: (provided) => ({
      ...provided,
      color: 'white',
    }),
  };

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="w-full max-w-5xl mx-auto">
        <header className="mb-8 glass glass-hover rounded-2xl p-6 text-center animate-slideInLeft">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 glass rounded-xl flex items-center justify-center mr-3">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">{who}'s Budget Entry</h1>
          </div>
          <p className="text-white/80">Track and categorize your daily expenses</p>
        </header>

        <div className="glass glass-hover rounded-2xl overflow-hidden animate-fadeIn">
          <div className="p-6">
            {message && (
              <div className={`mb-6 p-4 rounded-xl glass transition-all duration-300 animate-fadeIn ${
                message.includes('Error') 
                  ? 'border-red-400/30 text-red-200' 
                  : 'border-green-400/30 text-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.includes('Error') ? (
                      <svg className="h-5 w-5 text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-green-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">{message}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-white/60" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    name="date"
                    type="text"
                    value={formData.date}
                    onChange={handleChange}
                    placeholder="MM/DD"
                    className="glass-input w-full pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Amount (₪)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-white/60 font-medium">₪</span>
                  </div>
                  <input
                    name="amount"
                    type="number"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    className="glass-input w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Kids Cheshbon (₪)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-white/60 font-medium">₪</span>
                  </div>
                  <input
                    name="kidsCheshbon"
                    type="number"
                    value={formData.kidsCheshbon}
                    onChange={handleChange}
                    placeholder="Enter kid amount (optional)"
                    className="glass-input w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Reason</label>
                <input
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="What was this expense for?"
                  className="glass-input w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Category</label>
                <Select
                  name="category"
                  value={categories.find(option => option.value === formData.category) || null}
                  onChange={(option) => handleSelectChange(option, { name: 'category' })}
                  options={categories}
                  styles={customStyles}
                  placeholder="Select Category"
                  className="w-full"
                  classNamePrefix="react-select"
                  required
                  menuPosition="absolute"
                  menuShouldBlockScroll={true}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Payment Type</label>
                <Select
                  name="paymentType"
                  value={paymentTypes.find(option => option.value === formData.paymentType)}
                  onChange={(option) => handleSelectChange(option, { name: 'paymentType' })}
                  options={paymentTypes}
                  styles={customStyles}
                  placeholder="Select Payment Type"
                  className="w-full"
                  classNamePrefix="react-select"
                  required
                  menuPosition="absolute"
                  menuShouldBlockScroll={true}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">Kid</label>
                <Select
                  name="kid"
                  value={kids.find(option => option.value === formData.kid) || null}
                  onChange={(option) => handleSelectChange(option, { name: 'kid' })}
                  options={kids}
                  styles={customStyles}
                  placeholder="Select Kid (if applicable)"
                  className="w-full"
                  classNamePrefix="react-select"
                  isClearable
                  menuPosition="absolute"
                  menuShouldBlockScroll={true}
                />
              </div>

              <div className="md:col-span-2 mt-4">
                <button
                  type="submit"
                  disabled={isLoading || (!formData.amount && !formData.kidsCheshbon) || !formData.reason || !formData.category || (formData.kidsCheshbon && !formData.kid)}
                  className="glass-button w-full py-4 px-6 text-white font-semibold rounded-xl transition-all duration-300 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding Entry...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Expense Entry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-6 glass glass-hover rounded-2xl p-6 border-l-4 border-white/30">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Quick Tips
          </h3>
          <ul className="space-y-3 text-white/80 text-sm">
            <li className="flex items-start">
              <svg className="h-5 w-5 text-white/60 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>Date is pre-filled with today's date (MM/DD format)</span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-white/60 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>Use "Kids Cheshbon" for expenses paid from a kid's account - select a kid too!</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ExpenseEntry;